import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { EmptyState, InputIcon } from '@iziping/ui';
import type { Player } from '@iziping/common';
import { useSearchPlayers } from './playerHooks';
import { Search, UserPlus, Users } from 'lucide-react';

const SearchPlayers: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchTerm(value);
            }, 300),
        []
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const { data: players, isLoading, error } = useSearchPlayers(searchTerm);

    const handlePlayerClick = (playerId: number) => {
        navigate(`/players/${playerId}`);
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error</div>;

    return (
        <div className={'page'}>
            <InputIcon
                icon={Search}
                placeholder={t('PLAYER_SEARCH')}
                type={'text'}
                value={searchTerm}
                small={true}
                onChange={handleChange}
                onKeyUp={debouncedSearch}
            />

            {players && players.length > 0 && (
                <table className={'table-stripped'}>
                    <thead>
                        <tr>
                            <th>Edit</th>
                            <th>Prénom</th>
                            <th>Nom</th>
                            <th>Classement</th>
                            <th>Club</th>
                        </tr>
                    </thead>
                    <tbody>
                        {players.map((player: Player) => (
                            <tr key={player.id}>
                                <td onClick={() => handlePlayerClick(player.id ?? 0)}>
                                    <UserPlus />
                                </td>
                                <td>{player.firstname}</td>
                                <td>{player.lastname}</td>
                                <td>{player.points}</td>
                                <td>{player?.club?.name || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {searchTerm && players && players.length === 0 && (
                <EmptyState icon={<Users size={56} />} message={t('players.search.empty')} />
            )}
        </div>
    );
};

export default SearchPlayers;
